import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { lobbyCodeFromPath } from '../shared/lobbyCode.ts';
import { clearInvite, readInvite, rememberInvite } from './lib/pendingInvite.ts';
import { LoginButton } from './features/auth/LoginButton.tsx';
import { LobbyPage } from './features/lobby/LobbyPage.tsx';
import { WaitingRoom } from './features/lobby/WaitingRoom.tsx';
import { MatchPage } from './features/match/MatchPage.tsx';
import { useMatch } from './features/match/useMatch.ts';
import { CoachPage } from './features/coach/CoachPage.tsx';
import { PuzzlePage } from './features/puzzle/PuzzlePage.tsx';
import { LearnPage } from './features/learn/LearnPage.tsx';
import { ParityPage } from './features/parity/ParityPage.tsx';
import { ReviewScreen } from './features/review/ReviewScreen.tsx';
import { fetchMe, startGuest } from './lib/api.ts';
import type { Me } from './lib/api.ts';
import { MatchSocket } from './lib/socket.ts';

export interface AppProps {
  /** Injected in tests; production builds its own. */
  readonly socket?: MatchSocket;
}

export function App({ socket: injected }: AppProps = {}) {
  const socket = useMemo(() => injected ?? new MatchSocket(), [injected]);
  const [me, setMe] = useState<Me | null>(null);
  const [identityChecked, setIdentityChecked] = useState(false);
  const [guestPending, setGuestPending] = useState(false);
  // `/ws` rejects anonymous upgrades, so the socket only opens once signed in.
  const {
    view,
    queue,
    leaveQueue,
    hostLobby,
    joinLobby,
    cancelLobby,
    moveWaiter,
    startWith,
    kickWaiter,
    leaveLobby,
    returnToLobby,
    say,
    setName,
    drop,
    resign,
  } = useMatch(socket, me !== null);

  const refreshIdentity = useCallback(() => {
    const controller = new AbortController();
    fetchMe(controller.signal)
      .then((identity) => {
        setMe(identity);
        setIdentityChecked(true);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('identity check failed', error);
        setIdentityChecked(true);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => refreshIdentity(), [refreshIdentity]);

  /*
   * A viewer who scans the host's QR lands on `/j/CODE`. They should be in the
   * game without pressing anything — on a phone, mid-stream, every extra tap is
   * a viewer lost.
   *
   * Guarded by a ref rather than by state so a reconnect cannot re-fire the join
   * after the match has already started. The URL is cleaned up either way, so a
   * refresh does not try to re-join a lobby that is now a match.
   */
  const invite = useMemo(() => {
    const fromPath = lobbyCodeFromPath(window.location.pathname);
    if (fromPath !== null) {
      // Stash it before the URL is rewritten, so a reload during guest
      // sign-in does not lose the code.
      rememberInvite(fromPath);
      window.history.replaceState(null, '', '/');
      return fromPath;
    }
    // No code in the path: fall back to one stashed earlier this sitting.
    return readInvite();
  }, []);

  const inviteUsed = useRef(false);

  /*
   * An invited viewer is made a guest automatically.
   *
   * Someone who scans a QR off a stream has already expressed intent; asking
   * them to pick an identity first is the tap that loses them. They get a guest
   * account, join, and can sign in afterwards to keep the result.
   */
  useEffect(() => {
    if (invite === null || me !== null || !identityChecked || guestPending) return;
    setGuestPending(true);
    startGuest()
      .then((identity) => setMe(identity))
      .catch((error: unknown) => console.error('guest sign-in failed', error))
      .finally(() => setGuestPending(false));
  }, [invite, me, identityChecked, guestPending]);

  useEffect(() => {
    if (invite === null || inviteUsed.current) return;
    if (me === null || view.phase === 'connecting' || view.phase === 'resuming') return;

    inviteUsed.current = true;
    clearInvite();
    joinLobby(invite);
  }, [invite, me, view.phase, joinLobby]);

  const playAsGuest = useCallback(() => {
    setGuestPending(true);
    startGuest()
      .then((identity) => setMe(identity))
      .catch((error: unknown) => console.error('guest sign-in failed', error))
      .finally(() => setGuestPending(false));
  }, []);

  const inMatch = view.phase === 'playing' || view.phase === 'ended';

  /*
   * The review is a state of the match screen, not a place you navigate to.
   *
   * It closes itself when the phase moves on, so pressing "Play again" from
   * inside a review cannot leave an old game's analysis sitting over a live
   * board. The id is kept rather than the log: the server is the source of the
   * moves, so a reload or a reconnect reviews the same complete game.
   */
  const [reviewingMatchId, setReviewingMatchId] = useState<string | null>(null);
  useEffect(() => {
    if (view.phase !== 'ended') setReviewingMatchId(null);
  }, [view.phase]);

  /*
   * Practice is a place you go, not a state of the match screen.
   *
   * It needs no socket and no account, so it deliberately survives a
   * disconnection — but it closes the moment a real game starts, because a
   * practice board left sitting over a live match is how someone times out of
   * the game they are actually playing.
   */
  const [practising, setPractising] = useState(false);
  const [solving, setSolving] = useState(false);
  /*
   * The daily, held as an ISO date rather than a boolean.
   *
   * Taken in UTC so two people in different time zones comparing answers are
   * genuinely comparing the same board, which is the only reason a daily beats
   * a random puzzle.
   */
  const [dailyDate, setDailyDate] = useState<string | null>(null);
  const [learning, setLearning] = useState(false);
  const [counting, setCounting] = useState(false);
  useEffect(() => {
    if (inMatch || view.phase === 'waiting') {
      setPractising(false);
      setSolving(false);
      setLearning(false);
      setCounting(false);
      setDailyDate(null);
    }
  }, [inMatch, view.phase]);

  /**
   * What "another game" means depends on where this one came from.
   *
   * A host still owns their queue, so they step back into it. A challenger the
   * host pulled out of that queue goes back to the end of the line — they came
   * to play the streamer, and dropping them into public matchmaking would pair
   * them with a stranger and quietly take them out of the room. Only a player
   * who arrived through matchmaking looks for another stranger.
   */
  const playAgain = useCallback(() => {
    if (view.lobby) return returnToLobby();
    if (view.returnTo) return joinLobby(view.returnTo);
    return queue(view.spec.id);
  }, [view.lobby, view.returnTo, view.spec.id, returnToLobby, joinLobby, queue]);

  const playAgainLabel = view.lobby
    ? 'Next challenger'
    : view.returnTo
      ? 'Rejoin the queue'
      : 'Play again';

  // The lobby reads its name from /api/me rather than from the socket, so the
  // local copy is updated alongside the send; the server echoes `name_changed`
  // if it trims what was sent.
  const rename = useCallback(
    (name: string) => {
      setName(name);
      setMe((current) => (current ? { ...current, displayName: name } : current));
    },
    [setName],
  );

  // The spec only names the game once its rules are loaded; until then the id
  // is the honest label rather than the wrong game's title.
  const waitingGameName =
    view.waitingIn && view.waitingIn.game === view.spec.id
      ? view.spec.components.title
      : (view.waitingIn?.game ?? '');

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        Skip to the board
      </a>

      <header className="topbar">
        <div className="topbar__inner">
          <a className="brand" href="/">
            <span className="brand__mark" aria-hidden="true">
              <span className="brand__disc brand__disc--a" />
              <span className="brand__disc brand__disc--b" />
            </span>
            <span className="brand__name">Arena</span>
          </a>

          <div className="topbar__right">
            {/*
              Persistent chrome, deliberately NOT a live region.

              `StatusBar` is the single `aria-live` announcer for the whole app
              and already narrates connection loss ("Reconnecting to the
              arena."). A second polite region here would make every reconnect
              interrupt the game commentary a screen reader is already hearing,
              and the two would race. The visible badge stays for sighted users;
              its state is exposed to assistive technology on demand, not pushed.
            */}
            {/* There is no socket until sign-in, so reporting its state before
                then would be alarming and untrue. */}
            {me ? (
              <span className={`link-state link-state--${view.connection}`}>
                <span className="visually-hidden">Connection: </span>
                {view.connection === 'reconnecting' ? 'Reconnecting' : 'Connected'}
              </span>
            ) : null}
            {/* Signed out, the lobby already carries the single primary
                sign-in action; a second one here would compete with it. */}
            {identityChecked && me ? (
              <LoginButton me={me} onSignedOut={() => setMe(null)} />
            ) : null}
          </div>
        </div>
      </header>

      <main className="main" id="main">
        <div className="main__inner">
          {practising ? (
            <CoachPage spec={view.spec} onExit={() => setPractising(false)} />
          ) : dailyDate !== null ? (
            <PuzzlePage spec={view.spec} date={dailyDate} onExit={() => setDailyDate(null)} />
          ) : solving ? (
            <PuzzlePage spec={view.spec} onExit={() => setSolving(false)} />
          ) : learning ? (
            <LearnPage spec={view.spec} onExit={() => setLearning(false)} />
          ) : counting ? (
            <ParityPage spec={view.spec} onExit={() => setCounting(false)} />
          ) : reviewingMatchId !== null ? (
            <ReviewScreen
              matchId={reviewingMatchId}
              yourSeat={view.yourSeat}
              onExit={() => setReviewingMatchId(null)}
              exitLabel="Back to the result"
              onRematch={playAgain}
            />
          ) : inMatch ? (
            <MatchPage
              view={view}
              onDrop={drop}
              onResign={resign}
              // A host still owns their lobby: the game after this one comes
              // out of the queue they left standing, not out of matchmaking.
              // Otherwise re-queue the game just played rather than dropping
              // the player back to the reference game. Wrapped because this
              // reaches an onClick: passing `queue` directly hands it the
              // MouseEvent as the game id.
              onPlayAgain={playAgain}
              playAgainLabel={playAgainLabel}
              onSay={say}
              yourUserId={me?.userId ?? null}
              onReview={
                view.matchId === null ? undefined : () => setReviewingMatchId(view.matchId)
              }
            />
          ) : view.phase === 'resuming' ? (
            /*
              The server still has this account in a match and the board is on
              its way. Showing the lobby here is what stranded people: every
              action on it is refused while a match is live, so it reads as a
              dead end rather than as a moment of loading.
            */
            <section className="resuming" aria-labelledby="resuming-heading">
              <h2 id="resuming-heading" className="resuming__heading">
                <span className="spinner" aria-hidden="true" />
                Getting you back into your game
              </h2>
              <p className="resuming__note">
                You have a match in progress. Loading the board&hellip;
              </p>
            </section>
          ) : view.phase === 'waiting' && view.waitingIn ? (
            <WaitingRoom
              waiting={view.waitingIn}
              gameName={waitingGameName}
              onLeave={leaveLobby}
              view={view}
              yourName={me?.displayName ?? ''}
              onRename={rename}
              onSay={say}
              yourUserId={me?.userId ?? null}
            />
          ) : (
            <LobbyPage
              me={me}
              view={view}
              onQueue={queue}
              onLeaveQueue={leaveQueue}
              onHostLobby={hostLobby}
              onJoinLobby={joinLobby}
              onCancelLobby={cancelLobby}
              onMoveWaiter={moveWaiter}
              onStartWith={startWith}
              onKickWaiter={kickWaiter}
              onPractise={() => setPractising(true)}
              onSolvePuzzles={() => setSolving(true)}
              onDailyPuzzle={() => setDailyDate(new Date().toISOString().slice(0, 10))}
              onLearn={() => setLearning(true)}
              onCountParity={() => setCounting(true)}
              onPlayAsGuest={playAsGuest}
              startingGuest={guestPending}
              onSignedIn={(identity) => setMe(identity)}
              onRename={rename}
            />
          )}
        </div>
      </main>

      <footer className="footer">
        <div className="footer__inner">
          <p>Ratings are derived from the full match log and can be rebuilt at any time.</p>
        </div>
      </footer>
    </div>
  );
}
